import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography
} from '@mui/material';
import { apiError } from '../../Documents/format';
import { REASON_MAX, REASON_MIN } from '../constants';
import { isReasonValid, normalizeReason } from '../utils';

interface Props {
  open: boolean;
  title: string;
  /** Что именно произойдёт: показывается над полем причины */
  description?: React.ReactNode;
  confirmLabel: string;
  confirmColor?: 'primary' | 'success' | 'warning';
  onClose: () => void;
  /** Выполняет операцию; ошибка (throw) остаётся в диалоге, оператор может поправить причину и повторить */
  onSubmit: (reason: string) => Promise<void>;
}

const BORDER = '#e2e8f0';

/** Подтверждение операции с обязательной причиной («зачем»): причина попадает в журнал операций и журнал действий */
const ReasonDialog: React.FC<Props> = ({ open, title, description, confirmLabel, confirmColor = 'primary', onClose, onSubmit }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  const valid = isReasonValid(reason);
  const touched = reason.length > 0;

  const submit = useCallback(async () => {
    if (!isReasonValid(reason)) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(normalizeReason(reason));
      onClose();
    } catch (err: any) {
      setError(apiError(err, 'Не удалось выполнить операцию'));
    } finally {
      setSubmitting(false);
    }
  }, [reason, onSubmit, onClose]);

  return (
    <Dialog
      open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 20px 48px rgba(15, 23, 42, 0.16)' } }}
    >
      <DialogTitle sx={{ py: 1.5, px: 2.5, bgcolor: '#fff', borderBottom: `1px solid ${BORDER}` }}>
        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {description && <Typography variant="body2" component="div" sx={{ color: '#475569' }}>{description}</Typography>}
          <TextField
            autoFocus multiline minRows={2} maxRows={5} size="small" label="Причина" value={reason} disabled={submitting}
            onChange={e => setReason(e.target.value.slice(0, REASON_MAX))}
            error={touched && !valid}
            helperText={touched && !valid
              ? `Не короче ${REASON_MIN} символов`
              : 'Для чего выполняется операция. Причина сохраняется в журнале'}
            inputProps={{ maxLength: REASON_MAX }}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ py: 1.5, px: 2.5, borderTop: `1px solid ${BORDER}` }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none', fontWeight: 600 }}>Отмена</Button>
        <Button
          variant="contained" color={confirmColor} onClick={submit} disabled={!valid || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReasonDialog;
